/**
 * React hooks for Claude OAuth authentication.
 * Phase 3.7: Status polling, login SSE, logout, and audit log.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useRef } from "react";
import type {
  AuthStatus,
  LoginEvent,
  ClaudeAuthLogEntry,
} from "@black-wall/shared";

// ============================================================================
// Status Hook
// ============================================================================

interface AuthStatusResponse {
  status: AuthStatus;
}

export function useClaudeAuthStatus() {
  return useQuery({
    queryKey: ["claude-auth-status"],
    queryFn: async (): Promise<AuthStatus> => {
      const res = await fetch("/api/claude/auth/status");
      if (!res.ok) {
        throw new Error("Failed to fetch auth status");
      }
      const data = (await res.json()) as AuthStatusResponse;
      return data.status;
    },
    refetchInterval: 60_000, // Poll every minute
    staleTime: 30_000,
  });
}

// ============================================================================
// Login Hook (split start + events SSE + code submit)
// ============================================================================
//
// The Claude CLI's OAuth flow prints a URL whose redirect_uri is
// https://platform.claude.com/oauth/code/callback (NOT localhost). The user
// opens the URL, signs in, and platform.claude.com shows them a code. They
// paste that code back into our UI, which POSTs it to /api/claude/auth/login/code
// — the backend pipes it into the CLI subprocess stdin.
//
// The React hook keeps this as a state machine and attaches to a
// long-lived /login/events SSE stream so multiple tabs / a reload can keep
// observing the same session.

type LoginPhase =
  | "idle"
  | "starting"
  | "waiting_url"
  | "waiting_code"
  | "submitting"
  | "completed"
  | "error";

export type LoginMode = "session" | "setup-token";

interface LoginState {
  phase: LoginPhase;
  mode: LoginMode;
  events: LoginEvent[];
  deviceCodeUrl: string | null;
  setupToken: string | null;
  error: string | null;
  isCompleted: boolean;
}

const INITIAL_STATE: LoginState = {
  phase: "idle",
  mode: "session",
  events: [],
  deviceCodeUrl: null,
  setupToken: null,
  error: null,
  isCompleted: false,
};

export function useClaudeLogin() {
  const queryClient = useQueryClient();
  const [state, setState] = useState<LoginState>(INITIAL_STATE);
  const eventSourceRef = useRef<EventSource | null>(null);

  const closeEventSource = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const attachEventStream = useCallback(() => {
    closeEventSource();

    const es = new EventSource("/api/claude/auth/sse");
    eventSourceRef.current = es;

    es.onmessage = (msg) => {
      try {
        const event = JSON.parse(msg.data) as LoginEvent;
        setState((prev) => {
          const next: LoginState = {
            ...prev,
            events: [...prev.events, event],
          };
          if (event.type === "login_mode") {
            next.mode = event.mode;
          } else if (event.type === "device_code_received") {
            next.deviceCodeUrl = event.url;
            if (prev.phase === "starting" || prev.phase === "waiting_url") {
              next.phase = "waiting_code";
            }
          } else if (event.type === "waiting_for_authorization") {
            if (prev.phase !== "submitting") {
              next.phase = "waiting_code";
            }
          } else if (event.type === "authorized") {
            next.phase = "submitting";
          } else if (event.type === "setup_token_generated") {
            next.setupToken = event.token;
          } else if (event.type === "completed") {
            next.phase = "completed";
            next.isCompleted = true;
            queryClient.invalidateQueries({
              queryKey: ["claude-auth-status"],
            });
            queryClient.invalidateQueries({
              queryKey: ["claude-auth-log"],
            });
          } else if (event.type === "credentials_saved") {
            queryClient.invalidateQueries({
              queryKey: ["claude-auth-status"],
            });
          } else if (event.type === "error") {
            next.phase = "error";
            next.error = event.message;
          }
          return next;
        });
      } catch (err) {
        console.error("[useClaudeLogin] bad SSE event:", err);
      }
    };

    es.onerror = () => {
      // EventSource auto-reconnects on transient errors; only surface a
      // permanent failure when readyState stays at CLOSED.
      if (es.readyState === EventSource.CLOSED) {
        setState((prev) =>
          prev.phase === "completed"
            ? prev
            : { ...prev, phase: "error", error: "Connection lost" },
        );
      }
    };
  }, [closeEventSource, queryClient]);

  const startLogin = useCallback(
    async (mode: LoginMode = "session") => {
      setState({
        phase: "starting",
        mode,
        events: [],
        deviceCodeUrl: null,
        setupToken: null,
        error: null,
        isCompleted: false,
      });

      try {
        const res = await fetch(
          `/api/claude/auth/login?action=start&mode=${encodeURIComponent(mode)}`,
          {
            method: "POST",
          },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = (await res.json()) as {
          started: boolean;
          existing?: boolean;
          mode?: LoginMode;
          state?: string;
          url?: string | null;
        };

        // If an existing session is already past waiting_url, pre-fill the URL
        if (data.existing && data.url) {
          setState((prev) => ({
            ...prev,
            mode: data.mode ?? prev.mode,
            deviceCodeUrl: data.url ?? null,
            phase: "waiting_code",
          }));
        } else {
          setState((prev) => ({
            ...prev,
            mode: data.mode ?? prev.mode,
            phase: "waiting_url",
          }));
        }

        attachEventStream();
      } catch (err) {
        setState((prev) => ({
          ...prev,
          phase: "error",
          error: err instanceof Error ? err.message : "Unknown error",
        }));
      }
    },
    [attachEventStream],
  );

  const submitCode = useCallback(
    async (code: string) => {
      const trimmed = code.trim();
      if (!trimmed) {
        setState((prev) => ({ ...prev, error: "Code is empty" }));
        return;
      }

      setState((prev) => ({ ...prev, phase: "submitting", error: null }));

      try {
        const res = await fetch("/api/claude/auth/login?action=code", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: trimmed }),
        });

        const data = (await res.json()) as {
          ok: boolean;
          error?: string;
        };

        if (!res.ok || !data.ok) {
          throw new Error(data.error ?? `HTTP ${res.status}`);
        }
        // Actual completion comes via the SSE stream as 'completed'
      } catch (err) {
        setState((prev) => ({
          ...prev,
          phase: "error",
          error: err instanceof Error ? err.message : "Submit failed",
        }));
      }
    },
    [],
  );

  const cancel = useCallback(async () => {
    closeEventSource();
    try {
      await fetch("/api/claude/auth/login?action=abort", {
        method: "POST",
      });
    } catch {
      // ignore
    }
    setState((prev) => ({
      ...prev,
      phase: "idle",
      error: prev.error ?? "Cancelled",
    }));
  }, [closeEventSource]);

  const reset = useCallback(() => {
    closeEventSource();
    setState(INITIAL_STATE);
  }, [closeEventSource]);

  return {
    ...state,
    isLoading:
      state.phase === "starting" ||
      state.phase === "waiting_url" ||
      state.phase === "submitting",
    startLogin,
    submitCode,
    cancel,
    reset,
  };
}

// ============================================================================
// Logout Hook
// ============================================================================

export function useClaudeLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/claude/auth/logout", { method: "POST" });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error ?? "Logout failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claude-auth-status"] });
      queryClient.invalidateQueries({ queryKey: ["claude-auth-log"] });
    },
  });
}

// ============================================================================
// Auth Log Hook
// ============================================================================

interface AuthLogResponse {
  entries: ClaudeAuthLogEntry[];
}

export function useClaudeAuthLog(limit = 50) {
  return useQuery({
    queryKey: ["claude-auth-log", limit],
    queryFn: async (): Promise<ClaudeAuthLogEntry[]> => {
      const res = await fetch(`/api/claude/auth/log?limit=${limit}`);
      if (!res.ok) {
        throw new Error("Failed to fetch auth log");
      }
      const data = (await res.json()) as AuthLogResponse;
      return data.entries;
    },
    staleTime: 30_000,
  });
}
